import styled from 'styled-components'
import { format } from 'date-fns'
import { Calendar } from 'lucide-react'
import { formatDateOnly } from 'utils/formatting'
import { SampleRequest } from 'models/samples'
import { FbLink } from 'components/FbUI/FbLink'
import { TextPill } from 'components/FbUI/TextPill'
import { SampleStatusBadge } from './SampleStatusBadge'

interface MobileSampleCardProps {
  sample: SampleRequest
  onPreview?: (id: number) => void
}

const CardContainer = styled.div`
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
`

const CardRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #191d23;
  min-width: 70px;
`

const Value = styled.span`
  font-size: 14px;
  color: #191d23;
  flex: 1;
`

const RequestNumber = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0d6f4c;
  cursor: pointer;
  text-decoration: underline;
`

const DateRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
  padding: 10px;
  margin-top: 4px;
  background: #f9fafb;
  border-radius: 0 0 8px 8px;
`

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ProductPill = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid #e2e2e2;
  background: #fafafa;
  padding: 2px 8px;
`

const ProductName = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 1em;
  letter-spacing: 0.03em;
  color: #191d23;
`

const ExtraCount = styled.span`
  font-size: 11px;
  color: #6b7280;
  margin-left: 4px;
`

export function MobileSampleCard({ sample, onPreview }: MobileSampleCardProps) {
  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy h:mm a')
    } catch {
      return dateString
    }
  }

  // Get first product name and count of additional products
  const firstProduct = sample.line_items?.[0]?.product_name || '-'
  const additionalCount = (sample.line_items?.length || 1) - 1

  return (
    <CardContainer>
      <div className="p-3">
        {/* Request # and Status */}
        <CardRow>
          <Label>Request</Label>
          <RequestNumber onClick={() => onPreview?.(sample.id)}>
            #{sample.request_number}
          </RequestNumber>
          <SampleStatusBadge
            status={sample.status}
            label={sample.status_display}
          />
        </CardRow>

        <div className="h-px bg-gray-200 my-2" />

        {/* Company */}
        <CardRow>
          <Label>Company</Label>
          <Value>
            {sample.company_notification_link ? (
              <FbLink to={sample.company_notification_link}>
                {sample.company_name}
              </FbLink>
            ) : (
              sample.company_name || '-'
            )}
          </Value>
        </CardRow>

        {/* Product */}
        <CardRow>
          <Label>Product</Label>
          <ProductPill>
            <ProductName>{firstProduct}</ProductName>
          </ProductPill>
          {additionalCount > 0 && <ExtraCount>+{additionalCount}</ExtraCount>}
        </CardRow>

        {/* Requestor */}
        <CardRow>
          <Label>Requestor</Label>
          <TextPill backgroundColor="#E5E7EB" textColor="#374151">
            {sample.user_name}
          </TextPill>
        </CardRow>

        {/* Date footer */}
      </div>
      <DateRow>
        <DateInfo>
          <Calendar size={12} />
          <span>Created {formatDateTime(sample.created)}</span>
        </DateInfo>
        <span>Need by {formatDateOnly(sample.date_needed)}</span>
      </DateRow>
    </CardContainer>
  )
}
